import React, { memo, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { Dropdown, SlideUp, Text } from 'common-components'
import { useDropdownState } from 'hooks/useDropdownState'
import { useMobileInnerSliderCallbacks } from 'hooks/useMobileInnerSliderCallbacks'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { userSelectors } from 'store/features/user/selectors'
import { isPremiumModel, handlePremiumModelClick } from 'utils'
import CrownIcon from 'assets/svgr-icons/Crown.svg'
import { IAIImageModel } from 'store/types/aiImage'
import {
  LabelWrapper,
  CrownBadgeIcon
} from 'containers/SubSidebar/shared/GroupedModelDropdown'
import { IModelDropdownProps } from './types'
import ModelDropdownContent from './ModelDropdownContent'

const ModelDropdown: React.FC<IModelDropdownProps> = ({
  models,
  selectedModelCode,
  onModelSelect
}) => {
  const { openCallback, closeCallback } = useMobileInnerSliderCallbacks()
  const [isInnerSlideUpOpen, setIsInnerSlideUpOpen] = useState(false)
  const { isOpen, closeDropdown, toggleDropdown } = useDropdownState(
    openCallback,
    () => {
      closeCallback()
      setIsInnerSlideUpOpen(false)
    }
  )

  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const userCurrentPlanId = useSelector(userSelectors.userCurrentPlanId)

  const handleSelect = useCallback(
    (modelCode: string) => {
      const model = models.find((m: IAIImageModel) => m.code === modelCode)

      if (handlePremiumModelClick(model, userCurrentPlanId)) {
        return
      }

      if (modelCode !== selectedModelCode) {
        onModelSelect(modelCode)
      }
      closeDropdown()
    },
    [models, userCurrentPlanId, selectedModelCode, onModelSelect, closeDropdown]
  )

  const activeModel =
    models.find(model => model.code === selectedModelCode) || models[0]
  const isSelectedModelPremium = activeModel
    ? isPremiumModel(activeModel, userCurrentPlanId)
    : false

  if (isTablet && isOpen) {
    return createPortal(
      <SlideUp
        isOpen={isOpen}
        onClose={closeDropdown}
        isHidden={isInnerSlideUpOpen}
      >
        <ModelDropdownContent
          models={models}
          selectedModelCode={selectedModelCode}
          onSelect={handleSelect}
          userCurrentPlanId={userCurrentPlanId}
          onInnerSlideUpOpenChange={setIsInnerSlideUpOpen}
        />
      </SlideUp>,
      document.body
    )
  }

  return (
    <Dropdown
      headerContent={
        <LabelWrapper>
          <Text type="h2" color="secondarySemiDark">
            {activeModel?.name}
          </Text>
          {isSelectedModelPremium && (
            <CrownBadgeIcon
              icon={<CrownIcon />}
              size="small"
              color="goldDark"
            />
          )}
        </LabelWrapper>
      }
      mainContent={
        <ModelDropdownContent
          models={models}
          selectedModelCode={selectedModelCode}
          onSelect={handleSelect}
          userCurrentPlanId={userCurrentPlanId}
        />
      }
      isOpen={isOpen}
      toggleDropdown={toggleDropdown}
      closeDropdown={closeDropdown}
      forceBottom
    />
  )
}

export default memo(ModelDropdown)
